import queryString from 'query-string'
import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import {
  normalizeDetailResponse,
  normalizeListResponse,
  type PagedListResult,
} from '@/utils/apiResponseUtils'
import type {
  IDiaChiLayHang,
  IDiaChiLayHangPayload,
  TrangThaiDiaChiLayHang,
} from '@/interfaces/diaChiLayHangInterfaces'

const PREFIX = '/dia-chi-lay-hang'

function mapDiaChiLayHangFromApi(raw: Record<string, unknown>): IDiaChiLayHang {
  const trangThai = raw.trangThai
  const chiNhanh = raw.chiNhanh as
    | {
        id?: number
        ma?: string
        ten?: string
        khoVatLyId?: number | null
        khoVatLyMa?: string | null
        khoVatLyTen?: string | null
      }
    | null
    | undefined
  const chiNhanhIdRaw = raw.chiNhanhId ?? chiNhanh?.id
  const khoVatLyIdRaw = chiNhanh?.khoVatLyId ?? raw.khoVatLyId
  return {
    id: String(raw.id ?? ''),
    chiNhanhId: Number(chiNhanhIdRaw ?? 0),
    chiNhanhMa: chiNhanh?.ma != null ? String(chiNhanh.ma) : null,
    chiNhanhTen: chiNhanh?.ten != null ? String(chiNhanh.ten) : null,
    khoVatLyId:
      khoVatLyIdRaw != null && Number.isFinite(Number(khoVatLyIdRaw))
        ? Number(khoVatLyIdRaw)
        : null,
    khoVatLyMa:
      chiNhanh?.khoVatLyMa != null
        ? String(chiNhanh.khoVatLyMa)
        : raw.khoVatLyMa != null
          ? String(raw.khoVatLyMa)
          : null,
    khoVatLyTen:
      chiNhanh?.khoVatLyTen != null
        ? String(chiNhanh.khoVatLyTen)
        : raw.khoVatLyTen != null
          ? String(raw.khoVatLyTen)
          : null,
    dienThoai: raw.dienThoai != null ? String(raw.dienThoai) : null,
    diaChi: raw.diaChi != null ? String(raw.diaChi) : null,
    tinhThanh: raw.tinhThanh != null ? String(raw.tinhThanh) : null,
    quanHuyen: raw.quanHuyen != null ? String(raw.quanHuyen) : null,
    phuongXa: raw.phuongXa != null ? String(raw.phuongXa) : null,
    viDo:
      raw.viDo != null && Number.isFinite(Number(raw.viDo))
        ? Number(raw.viDo)
        : null,
    kinhDo:
      raw.kinhDo != null && Number.isFinite(Number(raw.kinhDo))
        ? Number(raw.kinhDo)
        : null,
    macDinh: Boolean(raw.macDinh ?? false),
    ghiChu: raw.ghiChu != null ? String(raw.ghiChu) : null,
    trangThai:
      trangThai === 'dangHoatDong' || trangThai === 'ngungHoatDong'
        ? trangThai
        : 'dangHoatDong',
    ngayTao: raw.ngayTao != null ? String(raw.ngayTao) : null,
    ngayCapNhat: raw.ngayCapNhat != null ? String(raw.ngayCapNhat) : null,
  }
}

function toPayload(body: IDiaChiLayHangPayload) {
  const payload: Record<string, unknown> = {
    chiNhanhId: Number(body.chiNhanhId),
  }
  if (body.dienThoai?.trim()) payload.dienThoai = body.dienThoai.trim()
  if (body.diaChi?.trim()) payload.diaChi = body.diaChi.trim()
  if (body.tinhThanh?.trim()) payload.tinhThanh = body.tinhThanh.trim()
  if (body.quanHuyen?.trim()) payload.quanHuyen = body.quanHuyen.trim()
  if (body.phuongXa?.trim()) payload.phuongXa = body.phuongXa.trim()
  if (body.viDo !== undefined) payload.viDo = body.viDo
  if (body.kinhDo !== undefined) payload.kinhDo = body.kinhDo
  if (body.macDinh !== undefined) payload.macDinh = body.macDinh
  if (body.ghiChu?.trim()) payload.ghiChu = body.ghiChu.trim()
  return payload
}

export function diaChiLayHangListQueryFromSearch(
  search: string,
): Record<string, string | number> {
  const q = queryString.parse(search.replace(/^\?/, ''))
  const out: Record<string, string | number> = {}
  const page = Number(q.page)
  const pageSize = Number(q.pageSize)
  if (Number.isFinite(page) && page > 0) out.page = page
  if (Number.isFinite(pageSize) && pageSize > 0) out.pageSize = pageSize
  const searchVal = q.search
  if (typeof searchVal === 'string' && searchVal.trim()) out.search = searchVal.trim()
  const tinh = q.tinhThanh
  if (typeof tinh === 'string' && tinh.trim()) out.tinhThanh = tinh.trim()
  const trangThai = q.trangThai
  if (trangThai === 'dangHoatDong' || trangThai === 'ngungHoatDong')
    out.trangThai = trangThai
  if (q.chiMacDinh === 'true' || q.chiMacDinh === '1') out.chiMacDinh = 'true'
  const chiNhanhId = Number(q.chiNhanhId)
  if (Number.isFinite(chiNhanhId) && chiNhanhId > 0) out.chiNhanhId = chiNhanhId
  const sort = q.sort
  if (typeof sort === 'string' && sort.trim()) out.sort = sort.trim()
  const sortDir = q.sortDir
  if (sortDir === 'asc' || sortDir === 'desc') out.sortDir = sortDir
  return out
}

export type DiaChiLayHangChiNhanhOption = {
  id: number
  label: string
  ma: string
  ten: string
  khoVatLyId: number | null
  khoVatLyMa: string | null
  khoVatLyTen: string | null
}

function labelChiNhanhWithKho(row: {
  ma?: string | null
  ten?: string | null
  khoVatLyMa?: string | null
  khoVatLyTen?: string | null
}): string {
  const cn = String(row.ten || row.ma || '').trim()
  const kho = String(row.khoVatLyTen || row.khoVatLyMa || '').trim()
  if (cn && kho) return `${cn} · ${kho}`
  return cn || kho || ''
}

/** Chi nhánh mảng hiện tại kèm kho vật lý (local HTChiNhanh). */
export function fetchChiNhanhOptionsDiaChiLayHang(): Promise<
  DiaChiLayHangChiNhanhOption[]
> {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/chi-nhanh-options`).then((res: unknown) => {
      const raw = normalizeDetailResponse(res)
      const list = Array.isArray(raw)
        ? raw
        : Array.isArray((raw as { data?: unknown })?.data)
          ? (raw as { data: unknown[] }).data
          : []
      const out: DiaChiLayHangChiNhanhOption[] = []
      for (const item of list) {
        if (!item || typeof item !== 'object') continue
        const row = item as Record<string, unknown>
        const id = Number(row.id)
        if (!Number.isFinite(id) || id <= 0) continue
        const ma = row.ma != null ? String(row.ma) : ''
        const ten = row.ten != null ? String(row.ten) : ''
        const khoVatLyId =
          row.khoVatLyId != null && Number.isFinite(Number(row.khoVatLyId))
            ? Number(row.khoVatLyId)
            : null
        const khoVatLyMa =
          row.khoVatLyMa != null ? String(row.khoVatLyMa) : null
        const khoVatLyTen =
          row.khoVatLyTen != null ? String(row.khoVatLyTen) : null
        out.push({
          id,
          ma,
          ten,
          khoVatLyId,
          khoVatLyMa,
          khoVatLyTen,
          label: labelChiNhanhWithKho({ ma, ten, khoVatLyMa, khoVatLyTen }),
        })
      }
      return out
    }),
  )
}

export function fetchDanhSachDiaChiLayHang(
  search: string,
): Promise<PagedListResult<IDiaChiLayHang>> {
  const params = diaChiLayHangListQueryFromSearch(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-dia-chi-lay-hang`, { params }).then(
      (res: unknown) => {
        const { data, paging } = normalizeListResponse(
          res as Parameters<typeof normalizeListResponse>[0],
        )
        return {
          data: (data as Record<string, unknown>[]).map(mapDiaChiLayHangFromApi),
          paging,
        }
      },
    ),
  )
}

export function fetchChiTietDiaChiLayHang(
  id: string | number,
): Promise<IDiaChiLayHang> {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-dia-chi-lay-hang`).then(
      (res: unknown) =>
        mapDiaChiLayHangFromApi(
          normalizeDetailResponse(res) as Record<string, unknown>,
        ),
    ),
  )
}

export function taoDiaChiLayHang(body: IDiaChiLayHangPayload) {
  return apiRequest(
    ApisServiceApp.post(`${PREFIX}/tao-dia-chi-lay-hang`, toPayload(body)),
  )
}

export function capNhatDiaChiLayHang(
  id: string | number,
  body: IDiaChiLayHangPayload,
) {
  return apiRequest(
    ApisServiceApp.put(
      `${PREFIX}/${id}/cap-nhat-dia-chi-lay-hang`,
      toPayload(body),
    ),
  )
}

export function xoaDiaChiLayHang(id: string | number) {
  return apiRequest(
    ApisServiceApp.delete(`${PREFIX}/${id}/xoa-dia-chi-lay-hang`),
  )
}

export type XoaNhieuDiaChiLayHangResult = {
  ok: number
  failed: Array<{ id: string; message: string }>
}

export async function xoaNhieuDiaChiLayHang(
  ids: string[],
): Promise<XoaNhieuDiaChiLayHangResult> {
  const uniqueIds = Array.from(
    new Set(ids.map((id) => String(id).trim()).filter(Boolean)),
  )
  const failed: XoaNhieuDiaChiLayHangResult['failed'] = []
  let ok = 0
  for (const id of uniqueIds) {
    try {
      await xoaDiaChiLayHang(id)
      ok += 1
    } catch (e) {
      failed.push({
        id,
        message: (e as { message?: string })?.message ?? '',
      })
    }
  }
  return { ok, failed }
}

export function chuyenTrangThaiDiaChiLayHang(
  id: string | number,
  trangThai: TrangThaiDiaChiLayHang,
) {
  return apiRequest(
    ApisServiceApp.patch(
      `${PREFIX}/${id}/chuyen-trang-thai-dia-chi-lay-hang`,
      { trangThai },
    ),
  )
}
